import * as React from 'react';
import { ITagOption } from '@cian/ui-kit';

import { FieldRow, FloorFilterWrapper } from '../../../components/Filters';
import { BannerLoader } from '../../../components/Filters/BannerLoader';
import { FilterAreaWrapper } from '../../../components/Filters/FilterAreaWrapper';
import { FiltersBannerWrapper } from '../../../components/Filters/FiltersBannerWrapper';
import { NewbuildingCompilationFilterWrapper } from '../../../components/Filters/NewbuildingCompilationFilterWrapper';
import { ApartmentTypeFilterWrapper } from '../../../components/Filters/ApartmentTypeFilterWrapper';
import { FiltersContentWrapper } from '../../../components/Filters/FiltersContentWrapper';
import { SelectedFiltersSection } from '../../../components/Filters/SelectedFiltersSection';
import { NO_BREAK_SPACE } from '../../../constants/symbolsUnicode';
import {
  ManifestationType,
  TotalAreaInput,
  KitchenAreaInput,
  LivingAreaInput,
  ApartmentType,
  HouseType,
  NewbuildingClass,
  UndergroundWalkTimeInput,
  HasDecoration,
  ConstructionStatus,
  IsSalesStart,
  IsSalesLeader,
  HasDiscountMortgage,
  DiscountPromoSearchType,
  MortgagePromoSearchType,
  RoomFilter,
  PriceFilter,
  FloorInput,
} from '../../Filters';
import { BannerAdditionalFiltersContainer } from '../../BannerAdditionalFiltersContainer';
import { FiltersGroup } from '../../../components/Filters/FiltersGroup';
import { FloorType } from '../../Filters/FloorType';
import { IStateTag } from '../../../actions/filterTags';

export interface IMoreFiltersModalContentProps {
  bannerLoaded: boolean;
  bannerError: boolean;
  handleAdfoxError: () => void;
  handleAdfoxMount: () => void;
  tags: IStateTag[];
  handleTagDelete: (option: ITagOption) => void;
  handleClearAllTags: () => void;
  getTagIcon: (tag: IStateTag) => React.ReactNode;
  hasUndreground: boolean;
  promoSearchTypesLoaded: boolean;
  discountMortgageEnabled: boolean;
}

export const MoreFiltersModalContent: React.FC<IMoreFiltersModalContentProps> = React.memo(
  ({
    bannerLoaded,
    bannerError,
    handleAdfoxError,
    handleAdfoxMount,
    tags,
    handleTagDelete,
    handleClearAllTags,
    getTagIcon,
    hasUndreground,
    promoSearchTypesLoaded,
    discountMortgageEnabled,
  }) => {
    return (
      <>
        {!bannerLoaded && <BannerLoader />}
        <FiltersContentWrapper isVisible={bannerLoaded}>
          {!bannerError && (
            <FiltersBannerWrapper>
              <BannerAdditionalFiltersContainer onError={handleAdfoxError} onLoad={handleAdfoxMount} />
            </FiltersBannerWrapper>
          )}

          <SelectedFiltersSection
            tags={tags}
            onTagDelete={handleTagDelete}
            onClearAll={handleClearAllTags}
            getTagIcon={getTagIcon}
          />

          <FiltersGroup title="О квартире">
            <FieldRow title="Комнаты" field={<RoomFilter />} dataTestId="RoomRow" />

            <FieldRow title="Цена" field={<PriceFilter />} dataTestId="PriceRow" />

            <FieldRow
              title="Площадь"
              dataTestId="SquareRow"
              field={
                <FilterAreaWrapper
                  totalArea={<TotalAreaInput />}
                  kitchenArea={<KitchenAreaInput />}
                  livingArea={<LivingAreaInput />}
                />
              }
            />

            <FieldRow title="Отделка" field={<HasDecoration />} dataTestId="DecorationRow" />

            <FieldRow
              title="Этаж"
              field={<FloorFilterWrapper floorInput={<FloorInput />} floorType={<FloorType />} />}
              dataTestId="FloorRow"
            />

            <FieldRow
              title="Апартаменты"
              field={<ApartmentTypeFilterWrapper apartmentType={<ApartmentType />} />}
              dataTestId="ApartmentTypeRow"
            />
          </FiltersGroup>

          <FiltersGroup title="О жилом комплексе и корпусе">
            <FieldRow title="Год сдачи" field={<ConstructionStatus />} dataTestId="ConstructionStatusRow" />
            <FieldRow title="Материал дома" field={<HouseType />} dataTestId="HouseTypeRow" />
            <FieldRow title="Класс жилья" field={<NewbuildingClass />} dataTestId="NewbuildingClassRow" />
            <FieldRow title="Кто продаёт" field={<ManifestationType />} dataTestId="ManifestationTypeRow" />

            {hasUndreground && (
              <FieldRow
                title="Пешком до метро"
                field={<UndergroundWalkTimeInput />}
                dataTestId="UndergroundWalkTimeRow"
              />
            )}
          </FiltersGroup>

          <FiltersGroup title="Выгодно">
            {promoSearchTypesLoaded && (
              <>
                <FieldRow
                  title="Ипотечные программы"
                  field={<MortgagePromoSearchType />}
                  dataTestId="advancedFilter_mortgagePromoSearchType"
                />
                <FieldRow
                  title={`Скидки и акции от${NO_BREAK_SPACE}застройщиков`}
                  field={<DiscountPromoSearchType />}
                  dataTestId="advancedFilter_discountPromoSearchType"
                />
              </>
            )}
            <FieldRow
              title="Подборки жилых комплексов"
              field={
                <NewbuildingCompilationFilterWrapper
                  salesStart={<IsSalesStart />}
                  salesLeader={<IsSalesLeader />}
                  discountMortgage={discountMortgageEnabled ? <HasDiscountMortgage /> : undefined}
                />
              }
              dataTestId="NewbuildingCompliationRow"
            />
          </FiltersGroup>
        </FiltersContentWrapper>
      </>
    );
  },
);

MoreFiltersModalContent.displayName = 'MoreFiltersModalContent';
